import gp, { JsonPointer, splitLast } from '@sagold/json-pointer';
import {
  Change,
  deepEqual,
  Editor,
  getData,
  getNodeList,
  isJsonError,
  JsonSchema,
  Node,
  Plugin,
  PluginEvent,
  PluginInstance,
  StringNode,
  updateNode,
} from '@sagold/react-json-editor';
import { v4 as uuid } from 'uuid';

import { EMPTY_VALUE } from '../../../components/table/valueFormatters';
import { isChangeEvent } from '../pluginHelper';

import {
  isCreatableSuggestionsSchema,
  shouldSkipInConditionalFilter,
} from './conditionalFilterSchema';

const SCHEMA_KEY = 'conditionalFilter' as keyof JsonSchema;

type TableFilter = { [p: string]: string | string[] | null };
export type Options = {
  values: string[];
  labels: (string | undefined)[];
};

export type FetchOptionsFunction = (filter: TableFilter) => Promise<{
  [p: keyof TableFilter]: Options;
}>;

/**
 * json-schema option, e.g.
 * {
 *   type: "string",
 *   enum: [],
 *   conditionalFilter: {
 *      dataProperty: "fundId"
 *   }
 * }
 */
export type SchemaOptions = {
  dataProperty: string;
  dataGroup?: string;
};

function addDuplicateLabelSuffixes(
  labels: Options['labels']
): Options['labels'] {
  const labelCounts = new Map<string, number>();

  return labels.map((label) => {
    if (label == null) {
      return label;
    }

    const count = labelCounts.get(label) ?? 0;
    labelCounts.set(label, count + 1);

    return count === 0 ? label : `${label} (${count + 1})`;
  });
}

/**
 * Add async conditional filtering to specified string-selections.
 * @todo extend functionality to work on conditionalFilter groups: `dataGroup`. Currently only supports a single group
 *
 * in json-editor pass an option
 *
 * ```
 * {
 *   pluginOptions: {
 *      conditionalFilter: {
 *        fetchOptions: typeof FetchOptionsFunction
 *     }
 *   },
 *   plugins: [ConditionalFilterPlugin]
 * }
 * ```
 *
 * for resolving dynamic select-options and register the corresponding input
 * elements with
 *
 * ```
 * {
 *   title: "Fund ID ",
 *   type: "string",
 *   enum: []
 *   conditionalFilter: {
 *     dataProperty: "fundId"
 *   }
 * }
 * ```
 */
export const ConditionalFilterPlugin: Plugin<{
  fetchOptions?: FetchOptionsFunction;
  options?: {
    isLoading?: boolean;
    skipPlugin?: boolean;
  };
}> = (he, { fetchOptions, options }) => {
  if (typeof fetchOptions !== 'function') {
    console.log(
      `ConditionalFilterPlugin: no callback 'fetchOptions' passed in 'pluginOptions.${SCHEMA_KEY}' `
    );
    return undefined;
  }

  // fetch schema definitions for conditional filtering
  const conditonalSchemaLocations: JsonPointer[] = [];
  he.draft.eachSchema((schema, pointer) => {
    if (schema[SCHEMA_KEY]) {
      conditonalSchemaLocations.push(pointer);
    }
  });

  if (conditonalSchemaLocations.length === 0) {
    console.log(
      `ConditionalFilterPlugin: no definitions found for '${SCHEMA_KEY}'`
    );
    return undefined;
  }

  // cache last requested filter for change-detection
  let lastFilter: TableFilter;

  // Information to handle async events
  const pluginId = uuid();
  let latestPendingRequestId: string;

  function removeDuplicatesFromPropertyOptions(
    propertyOptions?: Options
  ): Options | undefined {
    if (propertyOptions == null) {
      return propertyOptions;
    }
    const values: Options['values'] = [];
    const labels: Options['labels'] = [];
    for (const [index, value] of propertyOptions.values.entries()) {
      if (propertyOptions.values.indexOf(value) === index) {
        values.push(propertyOptions.values[index]);
        labels.push(propertyOptions.labels[index]);
      }
    }
    return {
      ...propertyOptions,
      values,
      labels,
    };
  }

  const pluginOptions = options;
  let hasCompletedInitialFetch = false;

  function applyStringPropertyOptions(
    schema: JsonSchema,
    propertyOptions: Options
  ) {
    schema.options = schema.options ?? {};
    const labels = addDuplicateLabelSuffixes(propertyOptions.labels);

    if (isCreatableSuggestionsSchema(schema)) {
      schema.options.suggestions = propertyOptions.values;
      schema.options.enum = labels;
      return;
    }

    schema.enum = propertyOptions.values;
    schema.options.enum = labels;
    if (
      Array.isArray(schema.type) &&
      schema.type.includes('null') &&
      Array.isArray(schema.enum) &&
      !schema.enum.includes(null)
    ) {
      schema.enum.push(null);
      schema.options.enum = [...labels, EMPTY_VALUE];
    }
  }

  async function updateConditionalFilter(
    filter: TableFilter,
    root: Node,
    isRefetch: boolean
  ) {
    setLoadingState(root, true, isRefetch);
    try {
      const requestId = uuid();
      latestPendingRequestId = requestId;
      const options = await fetchOptions?.(filter);

      // We only care about the results of the latest request being sent, because otherwise we might show outdated data
      // Scenario:
      // 1. Request: filter: {}, takes 20 seconds
      // 2. Request: filter: {fund: 1}, takes 5 seconds
      // The second request could be sent automatically e.g. when presetting a form with values. If we wouldn't check
      // whether the current response is from the latest request, we would show correct data after 5 seconds and then
      // overwrite it with incorrect data after 15 seconds. With the check below, the first request will be discarded,
      // no matter when the response was received.
      if (requestId !== latestPendingRequestId) {
        return;
      }

      // Ensure that the plugin is still registered to the editor
      // @todo: Find better ways to unregister plugins, that run async event listeners
      const registeredPluginId = he.plugins?.find(
        (p) => p.id === 'ConditionalFilterPlugin'
      )?.pluginId;

      if (registeredPluginId !== pluginId) {
        return;
      }

      const conditionalNodes = setLoadingState(root, false, isRefetch);
      const jsonSchema = he.draft.getSchema();

      // modify original json-schema
      for (const pointer of conditonalSchemaLocations) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const schema = gp.get<any>(jsonSchema, pointer);

        if (isRefetch && isCreatableSuggestionsSchema(schema)) {
          schema.options = {
            ...schema.options,
            loading: false,
          };
          gp.set(jsonSchema, pointer, schema);
          continue;
        }

        // @todo: fixme: options is not required on the schema, but this actually makes it required.
        schema.options = {
          ...schema.options,
          loading: false,
        };

        const property = schema[SCHEMA_KEY].dataProperty as string;
        const propertyOptions = removeDuplicatesFromPropertyOptions(
          options?.[property]
        );

        if (propertyOptions) {
          if (
            schema.type === 'string' ||
            (Array.isArray(schema.type) && schema.type.includes('string'))
          ) {
            applyStringPropertyOptions(schema, propertyOptions);
          } else if (
            schema.type === 'array' &&
            schema.items?.type === 'string'
          ) {
            // find the pointer to the actual editor field
            const [, conditionalNodePointer] = splitLast(pointer);
            const conditionalNode = conditionalNodes.find(
              (n) => n.pointer === `#/${conditionalNodePointer}`
            );
            // remove values that are no longer available
            // rje updateNode doesn't perform any checks and will still use values that are not available in options
            if (conditionalNode) {
              if (schema.options?.selectAll && !pluginOptions?.skipPlugin) {
                he.setData({
                  ...he.getData(),
                  [conditionalNode.property]: propertyOptions.values,
                });
              } else {
                const previousValue = getData(conditionalNode) as string[];
                if (Array.isArray(previousValue)) {
                  const updatedValue = previousValue.filter((val) =>
                    propertyOptions.values.includes(val)
                  );
                  he.setValue(conditionalNode.pointer, updatedValue);
                }
              }
            }

            schema.items.enum = propertyOptions.values;
            schema.items.options = schema.options ?? {};
            // allow option duplicates to keep option indices in sync with values, i.e. Distribution Notice (2)
            schema.items.options.enum = addDuplicateLabelSuffixes(
              propertyOptions.labels
            );
          } else {
            console.log(
              `ConditionalFilterPlugin: unsupported schema for ${pointer}`,
              schema
            );
          }

          // Write changes back to schema
          gp.set(jsonSchema, pointer, schema);
        }
      }

      // Write schema back to editor. On refetch, update the draft only — he.setSchema()
      // rebuilds the entire node tree (new node ids) and remounts all field widgets.
      if (jsonSchema != null && !isJsonError(jsonSchema)) {
        if (isRefetch) {
          he.draft.setSchema(jsonSchema);
        } else {
          he.setSchema(jsonSchema);
        }
      }

      // update nodes
      const oldState = he.getNode();
      const nodesToRecreate = isRefetch
        ? conditionalNodes.filter(
            (node) => !isCreatableSuggestionsSchema(node.schema)
          )
        : conditionalNodes;
      const [newState, changes] = recreateNodes(
        he.draft,
        oldState,
        nodesToRecreate
      );
      // set state and run update
      he.runPlugins(oldState, newState, changes);
      he.validate();
      hasCompletedInitialFetch = true;
    } catch (e) {
      console.log('ConditionalFilterPlugin: update failed', e);
      setLoadingState(root, false, isRefetch);
      he.validate();
    }
  }

  updateConditionalFilter({}, he.getNode(), false);

  const plugin: PluginInstance = {
    // id can be used to retrieve this plugin instance with editor.plugin("ConditionalFilterPlugin")
    id: 'ConditionalFilterPlugin',
    pluginId,
    onEvent(root: Node, event: PluginEvent) {
      if (options?.isLoading) {
        const nodes = getNodeList(root).map((node) => {
          node.options = node.options ?? {};
          node.options.loading = true;
          return node;
        }) as StringNode[];
        recreateNodes(he.draft, he.getNode(), nodes);
      }
      if (options?.skipPlugin) {
        return undefined;
      }
      if (!isChangeEvent(event)) {
        return undefined;
      }
      // get nodes for update
      const conditionalNodes = getNodeList(root).filter(
        (node) => node.schema[SCHEMA_KEY]
      );

      // build filter-set for request
      const dataFilter: TableFilter = {};
      for (const node of conditionalNodes) {
        if (shouldSkipInConditionalFilter(node.schema)) {
          continue;
        }
        const filterOptions = node.schema[SCHEMA_KEY] as SchemaOptions;
        if (filterOptions?.dataProperty && filterOptions?.dataGroup) {
          const property = filterOptions.dataProperty;
          // based on json-schema support we expect specific values here. Still,
          // there could be a data-input error so we:
          // @todo check if there are other possible data types that we need to support
          const data = getData(node);

          dataFilter[property] =
            typeof data === 'string' ||
            (Array.isArray(data) && data.every((d) => typeof d === 'string'))
              ? (getData(node) as string | string[])
              : [];

          // getData yields empty arrays for empty selections.
          // for portal-api an empty array is not equal to no filter, so we remove empty arrays here.
          if (
            Array.isArray(dataFilter[property]) &&
            !dataFilter[property]?.length
          ) {
            delete dataFilter[property];
          }
        }
      }
      // if filter set has not changed abort
      if (deepEqual(lastFilter, dataFilter)) {
        return undefined;
      }
      lastFilter = dataFilter;
      updateConditionalFilter(dataFilter, root, hasCompletedInitialFetch);
    },
  };
  return plugin;
};

function setLoadingState(root: Node, loading: boolean, isRefetch = false) {
  return getNodeList(root)
    .filter((node) => node.schema[SCHEMA_KEY])
    .filter((node) => !(isRefetch && isCreatableSuggestionsSchema(node.schema)))
    .map((node) => {
      node.options = node.options ?? {};
      node.options.loading = loading;
      return node;
    }) as StringNode[];
}

/**
 * recreate multiple nodes
 */
export function recreateNodes(
  draft: Editor['draft'],
  state: Node,
  nodes: StringNode[]
): [Node, Change[]] {
  let currentState = state;
  const changes: Change[] = [];
  for (const node of nodes) {
    const [result, _changes] = updateNode(draft, currentState, node.pointer);
    if (isJsonError(result)) {
      console.log(`ConditionalFilter: recreate node failed ${result.message}`);
      continue;
    }
    currentState = result;
    changes.push(...(_changes ?? []));
  }
  return [currentState, changes];
}
